import { MediaFile, ProcessingCommand } from '../types/media';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://backend-kiro.vercel.app/api';

export class VideoProcessor {
  private uploadedFilename: string | null = null;
  
  constructor() {
    // No need for canvas in this implementation
  }
  
  async processVideo(file: MediaFile, command: ProcessingCommand): Promise<MediaFile> {
    try {
      // For serverless demo, we'll use the file URL directly
      // In production, you'd upload to cloud storage first
      const fileUrl = file.url || URL.createObjectURL(file.file);
      
      let processedUrl: string;
      
      // Process based on command type
      switch (command.type) {
        case 'cut':
          processedUrl = await this.cutVideo(fileUrl, command.parameters);
          break;
        case 'filter':
          processedUrl = await this.applyVideoFilter(fileUrl, command.parameters, command);
          break;
        case 'speed':
          processedUrl = await this.changeVideoSpeed(fileUrl, command.parameters);
          break;
        case 'text':
          // For demo, we'll apply a filter instead since text overlay is more complex
          processedUrl = await this.applyVideoFilter(fileUrl, { filter: 'cinematic' });
          break;
        default:
          // Default to grayscale filter
          processedUrl = await this.applyVideoFilter(fileUrl, { filter: 'grayscale' });
      }
      
      // For demo purposes, return a simulated processed file
      // In production, you'd download from the processed URL
      const processedFilename = `processed_${file.name}`;
      
      return {
        ...file,
        url: processedUrl,
        name: processedFilename,
        processed: true
      };
    } catch (error) {
      console.error('Video processing failed:', error);
      throw new Error('Failed to process video');
    }
  }
  
  private async uploadFile(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'video');
    
    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result.filename;
  }
  
  private async cutVideo(fileUrl: string, parameters: any): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/process_video`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        operation: 'cut',
        file_url: fileUrl,
        parameters: {
          start_time: parameters.startTime || 0,
          end_time: parameters.endTime
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`Video cut failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result.output_url;
  }
  
  private async applyVideoFilter(fileUrl: string, parameters: any, command?: ProcessingCommand): Promise<string> {
    // Try using the AI command processor first
    if (command?.command) {
      try {
        const response = await fetch(`${API_BASE_URL}/process_command`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            command: command.command,
            file_url: fileUrl,
            file_type: 'video'
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          return result.output_url;
        }
      } catch (error) {
        console.warn('AI command processing failed, falling back to direct API');
      }
    }
    
    // Fallback to direct API calls
    let filterType = parameters.filter || 'grayscale';
    let intensity = parameters.intensity || 1.0;
    
    const response = await fetch(`${API_BASE_URL}/process_video`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        operation: 'filter',
        file_url: fileUrl,
        parameters: {
          filter_type: filterType,
          intensity
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`Video filter failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result.output_url;
  }
  
  private async changeVideoSpeed(fileUrl: string, parameters: any): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/process_video`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        operation: 'speed',
        file_url: fileUrl,
        parameters: {
          speed_factor: parameters.speed || 1.0
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`Video speed change failed: ${response.statusText}`);
    }
    
    const result = await response.json();
    return result.output_url;
  }
  
  private async downloadFile(filename: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/download/${filename}`);
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    
    return response.blob();
  }
}